import { Observable, Subscription, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import {
  PhxBreakpointObserverService,
  phxBadgeDefaultValues,
  PhxBadgeIntent,
  PhxBadgeContrast,
  Ellipses,
  ChevronRight,
  ChevronDown,
  Check,
  Hamburger,
  CrossUI,
  Bullet20,
  PersonGear,
  Dashboard,
} from 'phx-core';

import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  AfterViewInit,
  TemplateRef,
  ElementRef,
  OnDestroy,
  NgZone,
  ViewChild,
  Renderer2,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router, Event as RouteEvent, NavigationCancel, Params } from '@angular/router';

import { ISelectOption } from '@app/misc/hierarchy-multi-select-with-counter/hierarchy-multi-select-with-counter.component';
import { GlobalGroupsMultiSelectFilterComponent } from '@misc/groups-multi-select-filter/global.component';

import { Session } from '@services/session';
import { SidebarService, INavigation } from '@services/sidebar.service';
import { GroupsFilter } from '@services/groups-filter';
import { AdminFeatureFlagService } from '@app/services/feature-flags/admin';
import { PlatformFeatureFlagService } from '@services/feature-flags/platform';

import {
  IBreadcrumb,
  IBreadcrumbPart,
  IHeaderChanges,
  PageHeaderService,
  IHeaderShadowOnContentScrollElems,
  IBorderAndShadow,
} from './page-header.service';

import 'style-loader!./page-header.component.deep.scss';

export interface IPageHeaderTab {
  label?: string;
  link?: string | string[];
  template?: TemplateRef<any>;
  value?: string;
  shouldNavItemShow: boolean;
  isNew?: boolean;
  isBeta?: boolean;
  suffixTemplate?: TemplateRef<any>;
  disableQueryParamMode?: boolean;
  disableAutoSelect?: boolean;
  seperator?: boolean;
  notificationCount?: number;
  maxNotificationCount?: number;
  isActive?: boolean;
  shouldHandleMatrixParams?: boolean;
}

export interface IPageHeaderMobileMenuItem {
  label: string;
  /**
   * @deprecated `iconSrc` is deprecated and will be removed in the future.
   * We no longer need to show icons in the mobile menu.
   */
  iconSrc?: string;
  click?: () => void;
  shouldShowItem?: boolean;
  isDisabled?: boolean;
  key?: string;
  subtext?: string;
}

export interface IHeaderControlAction {
  type: string;
  payload?: any;
}

export const PAGE_HEADER_DATE_TIME_FORMAT = 'MMM DD, h:mm A';
export const PAGE_HEADER_DATE_FNS_FORMAT = 'MMM dd, h:mm a';

export const ADMIN_SWITCHER_ROUTES: Array<{ dashboard: string; admin: string }> = [
  {
    dashboard: '/fleetview',
    admin: '/admin/fleetview',
  },
  {
    dashboard: '/safety',
    admin: '/admin/safety',
  },
  {
    dashboard: '/compliance',
    admin: '/admin/compliance',
  },
  {
    dashboard: '/workforce',
    admin: '/admin/workforce',
  },
  {
    dashboard: '/fuel',
    admin: '/admin/fuel',
  },
  {
    dashboard: '/fleet-cards',
    admin: '/admin/fleet-cards',
  },
  {
    dashboard: '/maintenance',
    admin: '/admin/maintenance',
  },
  {
    dashboard: '/dispatch',
    admin: '/admin/dispatch',
  },
  {
    dashboard: '/messages',
    admin: '/admin/messaging-teams',
  },
];

@Component({
  selector: 'page-header',
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
})
export class PageHeaderComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  @Input() public breadcrumb?: IBreadcrumb;
  @Input() public titleForMobileView: string;
  @Input() public tabs: IPageHeaderTab[] = [];
  @Input() public isElevation2: boolean;
  @Input() public isFormHeader?: boolean;
  @Input() public hideBreadcrumbs?: boolean;
  @Input() public onFormCancel?: () => void;
  @Input() public onFormSubmit?: () => void;
  @Input() public isSubmitActionDisabled: boolean;
  @Input() public shouldShowBorder: boolean;
  @Input() public shouldHideActionsInMobileView: boolean;
  @Input() public mobileMenuItems: IPageHeaderMobileMenuItem[];
  @Input() public isLoading: boolean;
  @Input() public mobileDialogOpen: boolean = false;
  @Input() public groupFilterVisible: boolean = false;
  @Input() public isGlobalHeader: boolean = false; // TODO: remove once all headers are replaced
  @Input() public changeToBoxShadowOnScrollContainer: HTMLElement;
  /**
   * Previously there was no way to hide both shadow and border at the same time.
   * Either one of them was always visible. This property is added to allow hiding
   * both shadow and border at the same time.
   */
  @Input() public shouldHideShadowAndBorder: boolean = false;
  @Input() public hideAdminSwitcher: boolean = false;

  public rightExtrasTempRef: TemplateRef<unknown>;
  public selectedTab: IPageHeaderTab;
  public isTabsDropdownForMobileOpen: boolean = false;
  public useNzLinkRouter: boolean;
  public sidebarNav: INavigation = null;
  public groupsFilterDisabled$: Observable<boolean>;
  public selectedGroupOptions: ISelectOption[] = [];
  public shouldMatchExactLink: boolean = false;
  public isHidden: boolean = false;
  public isMobileView: boolean = false;
  public hasMobileMenuItems: boolean = false;
  public readonly EllipsesIcon: string = Ellipses;
  public readonly ChevronRightIcon: string = ChevronRight;
  public readonly ChevronDownIcon: string = ChevronDown;
  public readonly HamburgerIcon: string = Hamburger;
  public readonly CheckIcon: string = Check;
  public readonly CloseIcon: string = CrossUI;
  public readonly BulletIcon: string = Bullet20;
  public readonly PersonGearIcon: string = PersonGear;
  public readonly DashboardIcon: string = Dashboard;
  public readonly phxBadgeIntentDefaultValue: PhxBadgeIntent = phxBadgeDefaultValues.intent;
  public readonly phxBadgeContrastDefaultValue: PhxBadgeContrast = phxBadgeDefaultValues.contrast;
  public isHFTGroupFilterInPageHeaderEnabled: boolean;
  public isAdminPage: boolean = false;
  public adminSwitcherRoute: string = null;
  public isAdminSwitcherEnabled: boolean = false;

  private element: HTMLElement;
  private scrollEventListener: () => void;
  private shouldAlwaysShowMobileFooter: boolean = false;
  @ViewChild('headerExtra', { static: false }) private headerExtra: ElementRef;
  @ViewChild(GlobalGroupsMultiSelectFilterComponent) private groupFilter: GlobalGroupsMultiSelectFilterComponent;
  private routeChangeSubscription: Subscription;
  private previousRoute: string = '';
  private currentRoute: string = '';
  private resetTabOnResetCall: boolean = false;
  private mobileMenuItemsKeys: Set<string> = new Set();
  private readonly destroy$: Subject<void> = new Subject<void>();

  public get activeTabIndex(): number {
    if (this.useNzLinkRouter) {
      return null;
    }
    const index = this.tabs.findIndex(tab => tab.isActive);
    return index;
  }

  public get isUrlContainActiveTab(): boolean {
    return this.getTabIndexByUrl() !== -1;
  }

  public get selectedGroupsCount(): number {
    return this.groupFilter?.selectedOptionsCount;
  }

  public get groupsLabel(): string {
    return this.groupFilter?.filterLabel;
  }

  constructor(
    el: ElementRef,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private service: PageHeaderService,
    private session: Session,
    private sidebarService: SidebarService,
    private groupsFilterService: GroupsFilter,
    private zone: NgZone,
    private adminFeatureFlagService: AdminFeatureFlagService,
    private renderer: Renderer2,
    private phxBreakpointObserver: PhxBreakpointObserverService,
    fpFeatureFlagService: PlatformFeatureFlagService,
  ) {
    this.element = el.nativeElement;
    this.groupsFilterDisabled$ = this.groupsFilterService.groupsComponentDisable$;
    this.isAdminSwitcherEnabled = fpFeatureFlagService.isAdminSwitcherEnabled();
    this.routeChangeSubscription = this.router.events.subscribe((event: RouteEvent) => {
      if (event instanceof NavigationStart) {
        this.previousRoute = this.router.url;
        this.currentRoute = event.url;
      }

      if (event instanceof NavigationCancel) {
        this.setVisibleTabs(this.tabs, true);
      }

      if (event instanceof NavigationEnd) {
        const previousRouteWithoutQuery = this.removeQueryParameters(this.previousRoute);
        const currentRouteWithoutQuery = this.removeQueryParameters(this.currentRoute);
        if (this.resetTabOnResetCall && !this.isUrlContainActiveTab) {
          this.tabs = [];
          this.resetTabOnResetCall = false;
        }
        if (this.tabs?.length && previousRouteWithoutQuery !== currentRouteWithoutQuery) {
          this.setSelectedTab();
        }

        if (this.isAdminSwitcherEnabled) this.setAdminSwitcherRoute();
      }
    });
  }

  public ngOnChanges(): void {
    // Set tab based on partial url match
    if (this.tabs && this.tabs.length > 0) {
      this.setUseNzLinkRouter();
      this.setSelectedTab();
    }

    if (this.mobileMenuItems) {
      this.mobileMenuItems = this.mobileMenuItems.filter((item: IPageHeaderMobileMenuItem) => item.shouldShowItem);
    }
  }

  public ngOnInit() {
    this.setAdminSwitcherRoute();
    this.isHFTGroupFilterInPageHeaderEnabled = this.adminFeatureFlagService.isGroupFilterInNewHeaderEnabled();
    this.sidebarService.navListener.subscribe((nav: INavigation) => {
      this.sidebarNav = nav;
    });
  }

  public ngAfterViewInit(): void {
    this.service.changeHeaderContent$.pipe(filter(() => this.isGlobalHeader)).subscribe((changes: IHeaderChanges) => {
      if (changes) {
        this.resetTabOnResetCall = false;
        this.shouldHideActionsInMobileView = changes.shouldHideActionsInMobileView;
        this.shouldAlwaysShowMobileFooter = changes.shouldAlwaysShowMobileFooter;
        this.hasMobileMenuItems = this.mobileMenuItemsKeys.size > 0;
        this.shouldMatchExactLink = changes.shouldMatchExactLink ? changes.shouldMatchExactLink : false;
        this.shouldShowBorder = changes.shouldShowBorder;
        this.isElevation2 = changes.shouldShowShadow;
        this.shouldHideShadowAndBorder = !!changes.shouldHideShadowAndBorder;
        this.isFormHeader = !!changes.isFormHeader;
        this.hideBreadcrumbs = !!changes.hideBreadcrumbs;
        this.onFormCancel = changes.onFormCancel || undefined;
        this.onFormSubmit = changes.onFormSubmit || undefined;
        this.isSubmitActionDisabled = changes.isSubmitActionDisabled || false;
        this.hideAdminSwitcher = changes.hideAdminSwitcher || false;
        this.isLoading = false;

        if (changes.breadcrumbs) {
          this.breadcrumb = [...changes.breadcrumbs];
          const lastBreadcrumbPart: IBreadcrumbPart = this.breadcrumb[this.breadcrumb.length - 1];
          this.titleForMobileView = lastBreadcrumbPart.title;
        } else {
          this.breadcrumb = undefined;
        }

        if (changes.tabs) {
          this.setVisibleTabs(changes.tabs, true);
        } else {
          this.tabs = [];
        }

        if (changes.rightExtras) {
          this.rightExtrasTempRef = changes.rightExtras;
          setTimeout(() => this.setHeaderExtraHeight());
        } else {
          this.rightExtrasTempRef = undefined;
        }
      } else {
        this.resetTabOnResetCall = true;
        this.resetHeader();
      }
    });

    this.service.mobileMenuItem$
      .pipe(filter(() => this.isGlobalHeader))
      .subscribe((mobileMenuItem: IPageHeaderMobileMenuItem) => {
        if (this.mobileMenuItemsKeys.size !== 0) {
          if (!this.mobileMenuItemsKeys.has(mobileMenuItem.key)) {
            this.mobileMenuItems.push(mobileMenuItem);
          }
        } else {
          this.mobileMenuItems = [mobileMenuItem];
        }
        this.mobileMenuItemsKeys.add(mobileMenuItem.key);
        if (!this.shouldAlwaysShowMobileFooter) {
          this.hasMobileMenuItems = this.mobileMenuItems && this.mobileMenuItems.length > 0;
        }
      });

    this.service.removeMobileMenuItem$.pipe(filter(() => this.isGlobalHeader)).subscribe((mobileMenuItemKey: string) => {
      this.mobileMenuItemsKeys.delete(mobileMenuItemKey);
      this.mobileMenuItems = this.mobileMenuItems
        ? this.mobileMenuItems.filter((item: IPageHeaderMobileMenuItem) => item.key !== mobileMenuItemKey)
        : [];
      if (!this.shouldAlwaysShowMobileFooter) {
        this.hasMobileMenuItems = this.mobileMenuItems && this.mobileMenuItems.length > 0;
      }
    });

    this.service.showShadowOnContentScroll$
      .pipe(filter(() => this.isGlobalHeader))
      .subscribe((elems: IHeaderShadowOnContentScrollElems) => {
        if (!elems) return;

        if (this.scrollEventListener && this.changeToBoxShadowOnScrollContainer) {
          this.changeToBoxShadowOnScrollContainer.removeEventListener('scroll', this.scrollEventListener);
          this.changeToBoxShadowOnScrollContainer = null;
        }

        if (!elems.scrollContainer || !elems.intersectionElement) return;

        this.changeToBoxShadowOnScrollContainer = elems.scrollContainer;
        this.scrollEventListener = () => {
          const intersectionElementDims: DOMRect = elems.intersectionElement.getBoundingClientRect();
          const pageHeaderDims: DOMRect = this.element.getBoundingClientRect();

          this.zone.run(() => {
            if (intersectionElementDims.bottom < pageHeaderDims.bottom) {
              this.isElevation2 = true;
              this.shouldShowBorder = false;
            } else {
              this.isElevation2 = false;
              this.shouldShowBorder = true;
            }
          });
        };

        if (this.changeToBoxShadowOnScrollContainer) {
          this.zone.runOutsideAngular(() => {
            this.changeToBoxShadowOnScrollContainer.addEventListener('scroll', this.scrollEventListener, { passive: true });
          });
        }
      });

    this.service.changeBorderAndShadow$.subscribe((borderAndShadow: IBorderAndShadow) => {
      if (!borderAndShadow) return;

      this.shouldShowBorder = borderAndShadow.border;
      this.isElevation2 = borderAndShadow.shadow;
    });

    this.service.showHeader$.subscribe((showHeader: boolean) => {
      this.isHidden = !showHeader;
    });

    this.phxBreakpointObserver.smMobile.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.isMobileView = value;

      if (!value) {
        this.isTabsDropdownForMobileOpen = false;
        this.closeMobileDialog();
      }
    });
  }

  public badgeText(tab: IPageHeaderTab): string | null {
    if (tab.isBeta) return 'Beta';
    if (tab.isNew) return 'New';
    return null;
  }

  public notificationCountString(notificationCount: number): string {
    return notificationCount > 99 ? '99+' : notificationCount?.toString();
  }

  public trackByTab(index: number, tab: IPageHeaderTab): string {
    const tabLink = tab.link;
    let link = '';
    if (tabLink) {
      link = Array.isArray(tabLink) ? tabLink.join('') : tabLink;
    }
    return tab.label + link;
  }

  private setVisibleTabs(tabs: IPageHeaderTab[], checkEquality: boolean): void {
    const visibleTabs: IPageHeaderTab[] = tabs.filter((tab: IPageHeaderTab) => tab.shouldNavItemShow);
    if (checkEquality && !isEqual(this.tabs, visibleTabs)) {
      this.overrideTabs(visibleTabs);
    } else if (!checkEquality) {
      this.overrideTabs(visibleTabs);
    }

    if (this.tabs.length > 0) {
      this.setUseNzLinkRouter();
      this.setSelectedTab();
    }
  }

  private overrideTabs(tabs: IPageHeaderTab[]): void {
    this.tabs = tabs;
  }

  public ngOnDestroy(): void {
    if (this.changeToBoxShadowOnScrollContainer) {
      this.changeToBoxShadowOnScrollContainer.removeEventListener('scroll', this.scrollEventListener);
    }
    this.routeChangeSubscription.unsubscribe();

    this.destroy$.next();
    this.destroy$.complete();
  }

  public get groupFilterLabel(): string {
    const selectedGroupsCount: number = this.selectedGroupsCount;
    let label: string;
    if (selectedGroupsCount === 1) {
      label = this.groupsLabel;
    } else if (selectedGroupsCount > 1) {
      label = $localize`${selectedGroupsCount} Groups`;
    } else {
      label = $localize`All Groups`;
    }
    return label;
  }

  public get isMainPage(): boolean {
    return this.breadcrumb?.length === 1;
  }

  public get adminSwitcherTextContent(): string {
    return this.isAdminPage ? $localize`Go to Dashboard` : $localize`Go to Admin`;
  }

  public get isAdminSwitcherVisible(): boolean {
    return (
      this.isAdminSwitcherEnabled &&
      this.session.isDispatcher() &&
      this.adminSwitcherRoute &&
      !this.isFormHeader &&
      !this.hideAdminSwitcher &&
      this.isMainPage
    );
  }

  // It is used on if condition to check if the group filter is enabled
  public get isGroupFilterEnabled(): boolean {
    return this.session.isDispatcher() && this.isHFTGroupFilterInPageHeaderEnabled;
  }

  // It is used to hide the group filter with display none to avoid flickering and race condition issue on page change
  public get showGroupFilter(): boolean {
    return (
      this.session.isDispatcher() &&
      this.sidebarNav.hasGroups &&
      (!this.breadcrumb || this.isMainPage) &&
      !this.isFormHeader &&
      this.groupFilterVisible &&
      this.isHFTGroupFilterInPageHeaderEnabled
    );
  }

  public get showBreadcrumb(): boolean {
    return !this.hideBreadcrumbs && (!this.isFormHeader || (this.isFormHeader && this.tabs?.length > 0));
  }
  /**
   * `shouldShowBorder` and `shouldShowShadow` are used to determine whether the page header should have a border or shadow.
   * Both properties are mutually exclusive, meaning that if one is true, the other is false.
   * However, the PageHeaderService allows setting both to true or false.
   * This function has been added as a workaround to introduce minimum changes to the code.
   */
  public get showBorder(): boolean {
    return this.shouldShowBorder || !this.isElevation2;
  }

  public get shouldHideMobileFooter(): boolean {
    if (!this.rightExtrasTempRef) return true;
    return !this.shouldAlwaysShowMobileFooter && (this.shouldHideActionsInMobileView || this.hasMobileMenuItems);
  }

  public onAdminSwitcherClick(): void {
    this.router.navigate([this.adminSwitcherRoute]);
  }

  public isEmpty(val: string | string[]): boolean {
    return isEmpty(val);
  }

  public onTabIndexChanged(index: number): void {
    setTimeout(() => {
      // wait for a tick to allow tabs render properly.
      if (index >= 0) {
        this.setActiveTabByIndex(index);
      } else {
        this.tabs.forEach(tab => tab.isActive = false);
        this.selectedTab = undefined;
      }
      this.setSelectedTab();
    }, 100);
  }

  public onGroupsItemClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.openMobileDialog();
  }

  public openMobileDialog(): void {
    this.mobileDialogOpen = true;
  }

  public closeMobileDialog(): void {
    this.mobileDialogOpen = false;
  }

  public onTabChanged(value: string): void {
    const tabIndex: number = this.tabs.findIndex((tab: IPageHeaderTab) => tab.value === value);
    this.selectedTab = this.tabs[tabIndex];
    this.setActiveTabByIndex(tabIndex);
    this.service.setActiveTab(tabIndex);
    if (this.selectedTab?.shouldHandleMatrixParams && this.selectedTab.link?.length > 1) this.router.navigate([this.selectedTab.link[0], JSON.parse(this.selectedTab.link[1])]);
    this.isTabsDropdownForMobileOpen = false;
  }

  private setUseNzLinkRouter(): void {
    this.useNzLinkRouter = this.tabs.some(tab => !this.isEmpty(tab.link));
  }

  public setTabsDropdownForMobile(val: boolean): void {
    this.isTabsDropdownForMobileOpen = val;
  }

  public onSelectedOptionsChanged(selectedGroupOptions: Set<ISelectOption>): void {
    this.selectedGroupOptions = Array.from(selectedGroupOptions);
  }

  private setActiveTabByIndex(index: number): void {
    if (index < 0 || index >= this.tabs.length) {
      this.tabs.forEach((tab: IPageHeaderTab) => {
        tab.isActive = false;
      });
    } else {
      this.tabs.forEach((tab: IPageHeaderTab, i: number) => {
        tab.isActive = index === i;
      });
    }
  }

  private resetHeader(): void {
    this.breadcrumb = undefined;
    this.mobileMenuItems = [];
    this.mobileMenuItemsKeys.clear();
    this.rightExtrasTempRef = undefined;
    this.isLoading = true;
    this.cdr.detectChanges();
  }

  private setSelectedTab(): void {
    let index = -1;
    if (this.useNzLinkRouter) {
      index = this.getTabIndexByUrl();
    } else {
      index = this.tabs.findIndex(tab => tab.isActive);
      this.service.setActiveTab(index);
    }

    // If no tab is found or index is -1, set selectedTab to undefined
    if (index >= 0 && index < this.tabs.length) {
      this.selectedTab = this.tabs[index];
    } else {
      this.selectedTab = undefined;
      // Ensure no tab is marked as active
      this.tabs.forEach(tab => tab.isActive = false);
    }
  }

  private getTabIndexByUrl(): number {
    let index = 0;
    if (this.shouldMatchExactLink) {
      index = this.tabs.findIndex((tab: IPageHeaderTab): boolean => this.router.url === `/${tab.value}`);
    } else index = this.tabs.findIndex((tab: IPageHeaderTab): boolean => this.router.url.includes(tab.value));
    return index;
  }

  private removeQueryParameters(url: string): string {
    const queryIndex = url.indexOf('?');
    const semicolonIndex = url.indexOf(';');

    // It will convert "example.com?param1=1&param2=2" to "example.com"
    if (queryIndex !== -1) {
      return url.substring(0, queryIndex);
    }

    // It will convert "example.com;param1=1;param2=2" to "example.com"
    if (semicolonIndex !== -1) {
      return url.substring(0, semicolonIndex);
    }

    return url;
  }

  /**
   * Sets the CSS variable `--header-extra-height` to the height of the header extra element, which we can
   * use to adjust the height of the content to prevent it from hiding behind the header extra.
   */
  private setHeaderExtraHeight(): void {
    if (this.headerExtra) {
      const headerExtraHeight: number = this.headerExtra.nativeElement.offsetHeight;
      this.renderer.setProperty(document.documentElement, 'style', `--header-extra-height: ${headerExtraHeight}px`);
    }
  }

  private setAdminSwitcherRoute(): void {
    this.isAdminPage = this.router.url.includes('/admin');

    for (const route of ADMIN_SWITCHER_ROUTES) {
      if (this.router.url.includes(route.admin)) {
        this.adminSwitcherRoute = this.sidebarService.isRouteEnabled(route.dashboard) && route.dashboard;
        return;
      } else if (this.router.url.includes(route.dashboard)) {
        this.adminSwitcherRoute = this.sidebarService.isRouteEnabled(route.admin) && route.admin;
        return;
      } else {
        this.adminSwitcherRoute = null;
      }
    }
  }
}
